import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { DEFAULT_HOME } from '@/auth/routes'
import { Button } from '@/ui/Button'
import { InlineError } from '@/ui/states'
import { useOnboarding, useWizard } from './context'
import { metaFor, notConfigured, pathFor } from './steps'

/**
 * The finished screen — S-D2's `done`.
 *
 * Completion is claimed by the **server**: this screen posts `/complete` on
 * arrival rather than assuming the last step finished the wizard, because a
 * tenant can reach step 6 with steps 2 to 4 skipped and the required-step rule
 * lives in one place (`app/onboarding/steps.py`). Idempotent, so a reload does
 * not move the timestamp.
 *
 * "Waiting on you" lists what was skipped, and *Finish the skipped steps*
 * reopens the wizard on the first of them. Without that the only way back in
 * would be to create a second organisation.
 */
export function Finished(): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()

  const complete = useMutation(() => call(() => api.POST('/api/v1/onboarding/complete', {})))
  const reopen = useMutation(() => call(() => api.POST('/api/v1/onboarding/reopen', {})))

  const claimed = useRef(false)
  useEffect(() => {
    if (claimed.current || state.completed_at) return
    claimed.current = true
    void (async () => {
      const result = await complete.run(undefined)
      if (result.ok) adopt(result.data)
    })()
  }, [state.completed_at, complete, adopt])

  async function onReopen(): Promise<void> {
    const result = await reopen.run(undefined)
    if (result.ok) {
      adopt(result.data)
      void navigate(pathFor(result.data.current_step))
    }
  }

  // Not `progress.outstanding` — a skipped step is settled for the wizard and
  // outstanding for the tenant, and this panel is the tenant's view. See
  // `notConfigured`.
  const outstanding = notConfigured(state)
    .map((row) => metaFor(row.step))
    .filter((meta) => meta !== null)

  return (
    <div className="app-wiz" style={{ maxWidth: '44rem' }}>
      <section className="ar-card">
        <div className="ar-card__body">
          <InlineError failure={complete.state.failure ?? reopen.state.failure} />

          <div className="ar-empty">
            <div
              className="ar-empty__icon"
              style={{ background: 'var(--ar-success-bg)', color: 'var(--ar-success)' }}
              aria-hidden="true"
            >
              ✓
            </div>
            <p className="ar-empty__title">You&rsquo;re set up</p>
            <p className="ar-empty__body">
              Your first audit is running now. We&rsquo;ll email you when the first drafts are ready
              for approval — usually within a day.
            </p>
          </div>

          <div className="ar-grid ar-grid--3" style={{ marginTop: 'var(--ar-space-6)' }}>
            <div className="ar-panel">
              <strong>Running now</strong>
              <p
                className="ar-muted"
                style={{ margin: 'var(--ar-space-2) 0 0', fontSize: 'var(--ar-text-body-sm-size)' }}
              >
                Technical audit · {state.keywords.filter((row) => row.enabled).length} keywords
                tracked
              </p>
            </div>
            <div className="ar-panel">
              <strong>Tonight, 04:00</strong>
              <p
                className="ar-muted"
                style={{ margin: 'var(--ar-space-2) 0 0', fontSize: 'var(--ar-text-body-sm-size)' }}
              >
                First content briefs written
              </p>
            </div>
            <div className="ar-panel">
              <strong>Waiting on you</strong>
              <p
                className="ar-muted"
                style={{ margin: 'var(--ar-space-2) 0 0', fontSize: 'var(--ar-text-body-sm-size)' }}
              >
                {outstanding.length === 0
                  ? 'Nothing — every step is done'
                  : outstanding.map((meta) => meta.short).join(' · ')}
              </p>
            </div>
          </div>
        </div>

        <div className="ar-card__footer">
          <Button
            tone="primary"
            onClick={() => {
              void navigate(DEFAULT_HOME)
            }}
          >
            Go to my dashboard
          </Button>
          {outstanding.length > 0 ? (
            <Button
              busy={reopen.pending}
              busyLabel="Reopening setup"
              onClick={() => {
                void onReopen()
              }}
            >
              Finish the skipped steps
            </Button>
          ) : null}
        </div>
      </section>
    </div>
  )
}
